import { useMemo, useRef, useState } from "react";
import { Crosshair, Globe2, Hand, MapPin, Minus, Plus, Rotate3D, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AionSectionCard } from "@/components/aion/AionSectionCard";

const QUICK_DESTINATIONS = ["Prishtina", "Berlin", "Wien", "Dubai"];

const clamp = (value, min, max) => Math.min(max, Math.max(min, value));

export const AionWorldCommandCenter = ({ voiceDiagnostics }) => {
  const [query, setQuery] = useState("Prishtina");
  const [focusLabel, setFocusLabel] = useState("Prishtina");
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState({ x: -12, y: 18 });
  const [deviceLocation, setDeviceLocation] = useState(null);
  const [locationError, setLocationError] = useState("");
  const dragRef = useRef(null);

  const voiceReady = Boolean(
    voiceDiagnostics?.provider_configuration?.ready_for_live_voice
      || voiceDiagnostics?.display_state
      || voiceDiagnostics?.stt_provider,
  );

  const focusDescription = useMemo(() => {
    if (deviceLocation) {
      return `${deviceLocation.lat.toFixed(4)}, ${deviceLocation.lng.toFixed(4)} · vom Browser freigegeben`;
    }
    return `${focusLabel} · visueller Fokus, noch keine Live-Geocoding-Quelle verbunden`;
  }, [deviceLocation, focusLabel]);

  const selectDestination = (label) => {
    const next = String(label || "").trim();
    if (!next) return;
    setFocusLabel(next);
    setDeviceLocation(null);
    setLocationError("");
    setZoom((current) => Math.max(current, 1.35));
  };

  const requestDeviceLocation = () => {
    setLocationError("");
    if (!navigator.geolocation) {
      setLocationError("Dieser Browser stellt keine Standortfreigabe bereit.");
      return;
    }
    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        setDeviceLocation({ lat: coords.latitude, lng: coords.longitude });
        setFocusLabel("Dieses Gerät");
        setZoom(1.65);
      },
      () => setLocationError("Standort wurde nicht freigegeben oder konnte nicht bestimmt werden."),
      { enableHighAccuracy: false, timeout: 8000, maximumAge: 60000 },
    );
  };

  const onPointerDown = (event) => {
    dragRef.current = { x: event.clientX, y: event.clientY, rotation };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };

  const onPointerMove = (event) => {
    const start = dragRef.current;
    if (!start) return;
    setRotation({
      x: clamp(start.rotation.x - (event.clientY - start.y) * 0.18, -65, 65),
      y: start.rotation.y + (event.clientX - start.x) * 0.24,
    });
  };

  const stopDrag = () => {
    dragRef.current = null;
  };

  const changeZoom = (delta) => {
    setZoom((current) => clamp(Number((current + delta).toFixed(2)), 0.85, 2.2));
  };

  return (
    <div className="aion-view aion-view-stack" data-testid="aion-world-command-center">
      <AionSectionCard
        title="AION Command Center"
        eyebrow="WORLD · VOICE · VISION · SAFE CONTROL"
        subtitle="Weltansicht als zentrale Bedienebene für Standort, Zuhause, Kameras und AION. Gesten steuern hier zunächst nur die Oberfläche."
        testId="aion-world-command-card"
      >
        <div className="aion-world-layout">
          <div className="aion-world-stage-wrap">
            <div
              className="aion-world-stage"
              data-testid="aion-world-stage"
              onPointerDown={onPointerDown}
              onPointerMove={onPointerMove}
              onPointerUp={stopDrag}
              onPointerCancel={stopDrag}
              role="application"
              aria-label="Interaktiver AION Globus"
            >
              <div
                className="aion-world-globe"
                data-testid="aion-world-globe"
                style={{
                  "--aion-world-zoom": zoom,
                  "--aion-world-rotate-x": `${rotation.x}deg`,
                  "--aion-world-rotate-y": `${rotation.y}deg`,
                }}
              >
                <div className="aion-world-grid aion-world-grid-a" />
                <div className="aion-world-grid aion-world-grid-b" />
                <div className="aion-world-land aion-world-land-a" />
                <div className="aion-world-land aion-world-land-b" />
                <div className="aion-world-land aion-world-land-c" />
                <div className="aion-world-target" data-testid="aion-world-target"><span /></div>
              </div>
              <div className="aion-world-orbit aion-world-orbit-a" />
              <div className="aion-world-orbit aion-world-orbit-b" />
              <div className="aion-world-stage-label">
                <Globe2 size={18} />
                <div><strong>{focusLabel}</strong><small>{focusDescription}</small></div>
              </div>
            </div>

            <div className="aion-world-controls" data-testid="aion-world-controls">
              <Button type="button" variant="outline" size="sm" onClick={() => changeZoom(0.15)}><Plus size={15} /> Zoom</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => changeZoom(-0.15)}><Minus size={15} /> Zurück</Button>
              <Button type="button" variant="outline" size="sm" onClick={() => setRotation({ x: -12, y: 18 })}><Rotate3D size={15} /> Zentrieren</Button>
            </div>
          </div>

          <div className="aion-world-side">
            <div className="aion-world-search">
              <label htmlFor="aion-world-search-input">Ort fokussieren</label>
              <div className="aion-world-search-row">
                <Input
                  id="aion-world-search-input"
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") selectDestination(query);
                  }}
                  placeholder="z. B. Prishtina"
                  data-testid="aion-world-search-input"
                />
                <Button type="button" onClick={() => selectDestination(query)} data-testid="aion-world-focus-button"><Crosshair size={16} /> Fokus</Button>
              </div>
            </div>

            <div className="aion-world-quick-grid">
              {QUICK_DESTINATIONS.map((label) => (
                <button type="button" key={label} onClick={() => selectDestination(label)} data-testid={`aion-world-quick-${label.toLowerCase()}`}>
                  <MapPin size={14} /><span>{label}</span>
                </button>
              ))}
            </div>

            <Button type="button" variant="outline" onClick={requestDeviceLocation} data-testid="aion-world-device-location-button">
              <MapPin size={16} /> Standort dieses Geräts
            </Button>
            {locationError ? <div className="aion-error-box" data-testid="aion-world-location-error">{locationError}</div> : null}
          </div>
        </div>
      </AionSectionCard>

      <AionSectionCard
        title="Interaktionsstatus"
        eyebrow="AION PRESENCE"
        subtitle="Nur Funktionen mit echter technischer Grundlage werden als bereit angezeigt."
        testId="aion-world-status-card"
      >
        <div className="aion-compact-status-grid">
          <div className="aion-compact-status-box"><span>Voice</span><strong>{voiceReady ? "BEREIT" : "PRÜFUNG"}</strong></div>
          <div className="aion-compact-status-box"><span>Vision</span><strong>VORHANDEN</strong></div>
          <div className="aion-compact-status-box"><span>Handtracking</span><strong>NOCH NICHT AKTIV</strong></div>
          <div className="aion-compact-status-box"><span>Gesture Mode</span><strong>UI ONLY</strong></div>
          <div className="aion-compact-status-box"><span>Real Execution</span><strong>AUS</strong></div>
          <div className="aion-compact-status-box"><span>Trusted Actions</span><strong>AKTIV</strong></div>
        </div>
        <div className="aion-note aion-world-safety-note">
          <ShieldCheck size={17} />
          <div>
            <strong>Sicherheitsgrenze aktiv</strong>
            <p><Hand size={14} /> Maus, Touch und spätere Handgesten dürfen WORLD bewegen und auswählen. Reale Geräteaktionen bleiben weiterhin an AION Policy, Berechtigung und Bestätigung gebunden.</p>
          </div>
        </div>
      </AionSectionCard>
    </div>
  );
};
